import React, { useEffect, useState } from "react"
import { useDispatch } from "react-redux"
import { LuTrash2 } from "react-icons/lu"
import ConfirmModal from "../ConfirmModal/ConfirmModal"
import { getStatusChangeImpact } from "../../features/customer/customerSlice"
import { RESTORE_SKIP_TEXT } from "../../constants/demotionGuard"
import { formatMoney } from "../../utils/amount"
import { formatFullDateUTC } from "../../utils/date"

const MAX_REASON = 500

const StatusChangeConfirm = ({ open, setOpen, userId, status, onConfirm }) => {
  const dispatch = useDispatch()
  const [impact, setImpact] = useState(null)
  const [failed, setFailed] = useState(false)
  const [reason, setReason] = useState("")

  const statusId = status?._id

  useEffect(() => {
    if (!open) return undefined
    let active = true
    setImpact(null)
    setFailed(false)
    setReason("")
    Promise.resolve(dispatch(getStatusChangeImpact({ userId, statusId })))
      .then((result) => {
        if (!active) return
        if (!result || result.error) {
          setFailed(true)
          return
        }
        setImpact(result.payload || null)
      })
      .catch(() => {
        if (active) setFailed(true)
      })
    return () => {
      active = false
    }
  }, [dispatch, open, userId, statusId])

  const loading = !failed && !impact
  const needsReason = !!impact && impact.guard === true && impact.kind === "demotion" && impact.requiresReason
  const isMoveBack = !!impact && impact.guard === true && impact.kind === "moveBack"
  const trimmedReason = reason.trim()
  const reasonValid = trimmedReason.length >= 1 && trimmedReason.length <= MAX_REASON

  const handleConfirm = () => {
    if (needsReason) {
      return onConfirm({ confirm: true, reason: trimmedReason })
    }
    return onConfirm(undefined)
  }

  const renderBody = () => {
    if (loading) return "Checking what this change does…"
    if (failed) return "Could not check what this change does. You can still continue; the server will decide."
    if (needsReason) {
      const consequences = impact.consequences || {}
      return (
        <div>
          <ul className="list-disc pl-5 space-y-2 mb-4">
            {consequences.signedDocuments && <li>This client has signed documents.</li>}
            {consequences.billingStops && (
              <li>Billing is running and will stop: every active payment plan will be switched off.</li>
            )}
            {consequences.nextPayment && (
              <li>
                {`The next payment of ${formatMoney(consequences.nextPayment.amount)} due ${formatFullDateUTC(consequences.nextPayment.dueDate)} will not be charged.`}
              </li>
            )}
            {consequences.billingStops && (
              <li>Moving the client back to Active, MDL Credit Program or Retention will resume billing.</li>
            )}
          </ul>
          <label htmlFor="status-change-reason" className="block text-sm mb-1">Reason (required)</label>
          <textarea
            id="status-change-reason"
            className="w-full border border-divider rounded-lg p-2 bg-transparent text-text-primary"
            rows={3}
            maxLength={MAX_REASON}
            value={reason}
            onChange={(event) => setReason(event.target.value)}
          />
          <div className="text-right text-xs">{`${reason.length}/${MAX_REASON}`}</div>
        </div>
      )
    }
    if (isMoveBack) {
      const restore = impact.restore || {}
      if (restore.willResume) {
        return (
          <div>
            <p>Billing will resume.</p>
            {restore.nextPayment && (
              <p>{`The next payment of ${formatMoney(restore.nextPayment.amount)} is due ${formatFullDateUTC(restore.nextPayment.dueDate)}.`}</p>
            )}
          </div>
        )
      }
      return `Billing will not resume: ${RESTORE_SKIP_TEXT[restore.reason] || "the plan could not be restored"}. Check Payment Plan after the change.`
    }
    return "Are you sure to change status?"
  }

  const disabled = loading || (needsReason && !reasonValid)

  return (
    <ConfirmModal
      leftTopIcon={<LuTrash2 size={18} className="text-text-secondary" />}
      className="min-w-[400px] min-h-[200px]"
      title="Warning!"
      modalButtonTitle="Change"
      open={open}
      setOpen={setOpen}
      disabled={disabled}
      functionButton={handleConfirm}
    >
      {renderBody()}
    </ConfirmModal>
  )
}

export default StatusChangeConfirm
